// src/hooks/useConditionalAccessPolicies.ts
import { useEffect, useState } from 'react';
import { GlobalApi } from '@Apis';
import {
  ChangeUserConditionalAccessProps,
  ConditionalAccessPolicy,
  UserInfo,
} from '@Types/globalSetting';
import { useTranslation } from 'react-i18next';

export const useConditionalAccessPolicies = (
  dataSource: {
    cas?: boolean;
    v1?: boolean;
    mip?: boolean;
  },
  mipEnabled: boolean
) => {
  const [policies, setPolicies] = useState<ConditionalAccessPolicy[]>([]);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const conditionalAccessOptions = [
    { value: '', label: t('globalSetting.conditionalAccess.action.None') },
    {
      value: 'block:all',
      label: t('globalSetting.conditionalAccess.action.BlockAll'),
    },
    {
      value: 'block:office365',
      label: t('globalSetting.conditionalAccess.action.Block365'),
    },
    {
      value: 'mfa:all',
      label: t('globalSetting.conditionalAccess.action.RequireAll'),
    },
    {
      value: 'mfa:office365',
      label: t('globalSetting.conditionalAccess.action.Require365'),
    },
  ];

  const loadPolicies = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const data = (await GlobalApi.getConditionalAccessPolicy()).data;
      setPolicies(data);
    } catch (error) {
      console.error('Failed to load policies:', error);
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const refreshPolicies = async () => {
    await loadPolicies(true);
  };

  useEffect(() => {
    if (mipEnabled && dataSource.mip) {
      const initializePolicies = async () => {
        try {
          await loadPolicies(true);
        } catch (error) {
          console.error('Failed to initialize policies:', error);
        }
      };

      initializePolicies();
    } else {
      setPolicies([]);
      return;
    }
  }, [mipEnabled, dataSource.mip]);

  const getUserPolicy = (userPrincipalName: string): string => {
    for (const option of conditionalAccessOptions.slice(1)) {
      const [action, app] = option.value.split(':');
      const policy = policies.find(
        (p) =>
          p.action === action &&
          p.includeApp === app &&
          p.enabled &&
          p.users.some((u) => u.userPrincipalName === userPrincipalName)
      );
      if (policy) {
        return option.value;
      }
    }
    return '';
  };

  const getTargetPolicyId = (
    policyValue: string | undefined
  ): string | undefined => {
    if (!policyValue) return undefined;

    const [action, app] = policyValue.split(':');
    const policy = policies.find(
      (p) => p.action === action && p.includeApp === app && p.enabled
    );
    return policy?.id;
  };

  const updateUserPolicy = async (
    userInfo: UserInfo,
    currentValue: string,
    newValue: string,
    currentPolicyId?: string
  ) => {
    try {
      const requestBody: ChangeUserConditionalAccessProps = {};
      const targetPolicyId = getTargetPolicyId(newValue);
      if (currentPolicyId) {
        requestBody.sourcePolicyId = currentPolicyId;
      }

      if (targetPolicyId) {
        requestBody.targetPolicyId = targetPolicyId;
      }

      await GlobalApi.changeUserConditionalAccessPolicy(
        userInfo.userPrincipalName,
        requestBody
      );

      updateLocalPolicyState(userInfo, currentPolicyId, targetPolicyId);

      window.showNotification({
        titleMsg: t('riskUser.conditionalAccessPolicy.changeAction.success', {
          action:
            conditionalAccessOptions.find((option) => option.value === newValue)
              ?.label || 'Unknown',
        }),
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Failed to update user policy:', error);
      if (['404', 404].includes(error?.status)) {
        const target = error?.data.error.details[0].target;
        let bodyMsg = '';
        if ('sourcePolicyId' === target) {
          bodyMsg = t(
            'riskUser.conditionalAccessPolicy.changeAction.error.body.notFoundSource',
            {
              action:
                conditionalAccessOptions.find(
                  (option) => option.value === currentValue
                )?.label || 'Unknown',
            }
          );
        } else if ('targetPolicyId' === target) {
          bodyMsg = t(
            'riskUser.conditionalAccessPolicy.changeAction.error.body.notFoundTarget',
            {
              action:
                conditionalAccessOptions.find(
                  (option) => option.value === newValue
                )?.label || 'Unknown',
            }
          );
        } else if ('userPrincipalName' === target) {
          bodyMsg = t(
            'riskUser.conditionalAccessPolicy.changeAction.error.body.notFoundUser',
            {
              user: userInfo.userPrincipalName,
            }
          );
        }

        window.showNotification({
          titleMsg: t(
            'riskUser.conditionalAccessPolicy.changeAction.error.title'
          ),
          bodyMsg,
          severity: 'error',
        });
        return;
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    }
  };

  const updateLocalPolicyState = (
    userInfo: UserInfo,
    oldPolicyId?: string,
    newPolicyId?: string
  ) => {
    setPolicies((prevPolicies) => {
      const updatedPolicies = [...prevPolicies];

      // 1. remove user from old policy (if exists)
      if (oldPolicyId) {
        const oldPolicyIndex = updatedPolicies.findIndex(
          (p) => p.id === oldPolicyId
        );
        if (oldPolicyIndex !== -1) {
          updatedPolicies[oldPolicyIndex] = {
            ...updatedPolicies[oldPolicyIndex],
            users: updatedPolicies[oldPolicyIndex].users.filter(
              (u) => u.userPrincipalName !== userInfo.userPrincipalName
            ),
          };
        }
      }

      // 2. add user to new policy (if exists)
      if (newPolicyId) {
        const newPolicyIndex = updatedPolicies.findIndex(
          (p) => p.id === newPolicyId
        );
        if (newPolicyIndex !== -1) {
          // 检查用户是否已存在，避免重复添加
          const existingUser = updatedPolicies[newPolicyIndex].users.find(
            (u) => u.userPrincipalName === userInfo.userPrincipalName
          );
          if (!existingUser) {
            updatedPolicies[newPolicyIndex] = {
              ...updatedPolicies[newPolicyIndex],
              users: [...updatedPolicies[newPolicyIndex].users, userInfo],
            };
          }
        }
      }

      return updatedPolicies;
    });
  };

  return {
    conditionalAccessOptions,
    policies,
    loading,
    getUserPolicy,
    updateUserPolicy,
    refreshPolicies,
  };
};
